import { Link, router, useForm } from "@inertiajs/react"
import { ExternalLink, Trash2 } from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Textarea } from "@/components/ui/textarea"
import type { CategoryOption, StudioPoiRow, StudioUrls } from "@/lib/types"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PoiSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  poi: StudioPoiRow | null // null = create mode
  categories: CategoryOption[]
  urls: StudioUrls
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PoiSheet({ open, onOpenChange, poi, categories, urls }: PoiSheetProps) {
  const isEdit = poi !== null

  const form = useForm({
    name: poi?.name ?? "",
    description: poi?.description ?? "",
    category_ids: poi?.categories.map((c) => c.id) ?? ([] as number[]),
    address: poi?.address ?? "",
    phone: poi?.phone ?? "",
    website_url: poi?.website_url ?? "",
    booking_url: poi?.booking_url ?? "",
    google_maps_url: poi?.google_maps_url ?? "",
    lat: poi?.lat != null ? String(poi.lat) : "",
    lng: poi?.lng != null ? String(poi.lng) : "",
  })

  // Nest under "poi" key for Rails strong params
  form.transform((data) => ({ poi: data }))

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    if (isEdit) {
      form.patch(poi.update_url, {
        onSuccess: () => onOpenChange(false),
        preserveScroll: true,
      })
    } else {
      form.post(urls.create_poi, {
        onSuccess: () => onOpenChange(false),
        preserveScroll: true,
      })
    }
  }

  function performDelete() {
    if (!poi) return

    router.delete(poi.destroy_url, {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
    })
  }

  function toggleCategory(categoryId: number) {
    const current = form.data.category_ids
    if (current.includes(categoryId)) {
      form.setData("category_ids", current.filter((id) => id !== categoryId))
    } else {
      if (current.length >= 3) return // max 3 categories
      form.setData("category_ids", [...current, categoryId])
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[620px]" showCloseButton>
        <SheetHeader>
          <SheetTitle>{isEdit ? `Edit · ${poi.name}` : "Add a new POI"}</SheetTitle>
          <SheetDescription>
            Public POI visible to every property in this region
          </SheetDescription>
        </SheetHeader>

        <form
          id="poi-sheet-form"
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 overflow-y-auto px-4"
        >
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="poi-name">Name *</Label>
            <Input
              id="poi-name"
              value={form.data.name}
              onChange={(e) => form.setData("name", e.target.value)}
              placeholder="e.g. Miradouro da Graça"
              required
            />
            {form.errors.name && (
              <p className="text-sm text-destructive">{form.errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="poi-description">Description</Label>
            <Textarea
              id="poi-description"
              value={form.data.description}
              onChange={(e) => form.setData("description", e.target.value)}
              placeholder="Brief description of this place..."
              rows={3}
            />
            {form.errors.description && (
              <p className="text-sm text-destructive">{form.errors.description}</p>
            )}
          </div>

          {/* Categories */}
          <div className="space-y-1.5">
            <Label>
              Categories{" "}
              <span className="font-normal text-muted-foreground">(max 3)</span>
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {categories.map((cat) => {
                const selected = form.data.category_ids.includes(cat.id)
                return (
                  <Button
                    key={cat.id}
                    type="button"
                    variant={selected ? "default" : "outline"}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => toggleCategory(cat.id)}
                  >
                    {cat.icon && <span className="mr-1">{cat.icon}</span>}
                    {cat.name}
                  </Button>
                )
              })}
            </div>
            {form.errors.category_ids && (
              <p className="text-sm text-destructive">{form.errors.category_ids}</p>
            )}
          </div>

          {/* Address */}
          <div className="space-y-1.5">
            <Label htmlFor="poi-address">Address</Label>
            <Input
              id="poi-address"
              value={form.data.address}
              onChange={(e) => form.setData("address", e.target.value)}
              placeholder="Street address"
            />
            {form.errors.address && (
              <p className="text-sm text-destructive">{form.errors.address}</p>
            )}
          </div>

          {/* Phone */}
          <div className="space-y-1.5">
            <Label htmlFor="poi-phone">Phone</Label>
            <Input
              id="poi-phone"
              value={form.data.phone}
              onChange={(e) => form.setData("phone", e.target.value)}
              placeholder="+351 ..."
            />
            {form.errors.phone && (
              <p className="text-sm text-destructive">{form.errors.phone}</p>
            )}
          </div>

          {/* Website URL */}
          <div className="space-y-1.5">
            <Label htmlFor="poi-website-url">Website URL</Label>
            <Input
              id="poi-website-url"
              value={form.data.website_url}
              onChange={(e) => form.setData("website_url", e.target.value)}
              placeholder="https://..."
            />
            {form.errors.website_url && (
              <p className="text-sm text-destructive">{form.errors.website_url}</p>
            )}
          </div>

          {/* Booking URL */}
          <div className="space-y-1.5">
            <Label htmlFor="poi-booking-url">Booking URL</Label>
            <Input
              id="poi-booking-url"
              value={form.data.booking_url}
              onChange={(e) => form.setData("booking_url", e.target.value)}
              placeholder="https://..."
            />
            {form.errors.booking_url && (
              <p className="text-sm text-destructive">{form.errors.booking_url}</p>
            )}
          </div>

          {/* Google Maps URL */}
          <div className="space-y-1.5">
            <Label htmlFor="poi-google-maps-url">Google Maps URL</Label>
            <Input
              id="poi-google-maps-url"
              value={form.data.google_maps_url}
              onChange={(e) => form.setData("google_maps_url", e.target.value)}
              placeholder="https://maps.google.com/..."
            />
            {form.errors.google_maps_url && (
              <p className="text-sm text-destructive">{form.errors.google_maps_url}</p>
            )}
          </div>
        </form>

        {isEdit && (
          <div className="border-t px-1 py-3">
            <Link
              href={poi.edit_url}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              <ExternalLink className="size-3.5" />
              Manage photos and media
              {poi.photos_count > 0 && (
                <span className="text-xs">({poi.photos_count})</span>
              )}
            </Link>
          </div>
        )}

        <SheetFooter className="flex-row justify-between border-t pt-4">
          <div>
            {isEdit && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="destructive" size="sm">
                    <Trash2 className="mr-1.5 size-4" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this POI?</AlertDialogTitle>
                    <AlertDialogDescription>
                      “{poi.name}” will be removed from every guide in this
                      region. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={performDelete}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete POI
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="poi-sheet-form"
              size="sm"
              disabled={form.processing}
            >
              {form.processing
                ? "Saving..."
                : isEdit
                  ? "Save changes"
                  : "Create POI"}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
